import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { accountingApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

/**
 * AP Aging — FR-ACC-04.06
 * What we owe suppliers, bucketed by days past due.
 */
export function ApAgingPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [asOfDate, setAsOfDate] = useState(today);
  const [sortBy, setSortBy] = useState<'total' | 'overdue'>('overdue');

  const q = useQuery({
    queryKey: ['ap', 'aging', asOfDate],
    queryFn: () => accountingApi.apAging(asOfDate),
  });

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Accounting · Payables"
        title="AP aging"
        subtitle="What the business owes to suppliers. Prioritize payments to avoid late fees and preserve relationships."
      />

      <div className="card p-4 mb-6 flex gap-4 items-end border-slate-200">
        <div>
          <label className="label">As of date</label>
          <input
            type="date"
            className="input w-44"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
          />
        </div>
        <div className="text-xs text-slate-500 max-w-sm">
          Aging is calculated from each vendor invoice's due date. Overdue buckets are candidates for payment runs.
        </div>
      </div>

      {q.isLoading ? (
        <div className="card"><LoadingState rows={5} /></div>
      ) : q.error ? (
        <ErrorState error={q.error} />
      ) : (
        <ApAgingBody data={q.data} sortBy={sortBy} setSortBy={setSortBy} />
      )}
    </div>
  );
}

function ApAgingBody({
  data, sortBy, setSortBy,
}: { data: any; sortBy: 'total' | 'overdue'; setSortBy: (s: 'total' | 'overdue') => void }) {
  const totals = data.totals;

  const sortedSuppliers = useMemo(() => {
    const rows = [...(data.suppliers ?? [])];
    if (sortBy === 'overdue') {
      rows.sort((a, b) =>
        (b.b1_30 + b.b31_60 + b.b61_90 + b.b90_plus) -
        (a.b1_30 + a.b31_60 + a.b61_90 + a.b90_plus)
      );
    } else {
      rows.sort((a, b) => b.total - a.total);
    }
    return rows;
  }, [data.suppliers, sortBy]);

  if (!data.suppliers?.length) {
    return (
      <div className="card">
        <EmptyState
          message="Nothing payable"
          hint="No outstanding AP balances as of this date."
        />
      </div>
    );
  }

  const overdueTotal = totals.b1_30 + totals.b31_60 + totals.b61_90 + totals.b90_plus;

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total payable" value={`${fmtCurrency(totals.total)}`} accent="finance" />
        <KpiTile label="Current (not due)" value={`${fmtCurrency(totals.current)}`} />
        <KpiTile label="Overdue" value={`${fmtCurrency(overdueTotal)}`}
          accent={overdueTotal > 0 ? 'amber' : 'default'}
          sublabel={totals.total > 0 ? `${((overdueTotal / totals.total) * 100).toFixed(0)}% of total` : undefined} />
        <KpiTile label="90+ days" value={`${fmtCurrency(totals.b90_plus)}`}
          accent={totals.b90_plus > 0 ? 'danger' : 'default'} />
      </div>

      <div className="card p-5 mb-4 border-slate-200">
        <div className="eyebrow text-slate-500 mb-3">Aging distribution</div>
        <StackedBucketBar totals={totals} />
        <div className="grid grid-cols-5 gap-2 mt-4">
          <BucketLegend label="Current"    value={totals.current}  color="bg-slate-400" />
          <BucketLegend label="1-30 days"  value={totals.b1_30}    color="bg-amber-400" />
          <BucketLegend label="31-60 days" value={totals.b31_60}   color="bg-amber-600" />
          <BucketLegend label="61-90 days" value={totals.b61_90}   color="bg-orange-600" />
          <BucketLegend label="90+ days"   value={totals.b90_plus} color="bg-danger" />
        </div>
      </div>

      <div className="card p-1.5 mb-4 flex gap-1 items-center border-slate-200">
        <span className="eyebrow text-slate-500 px-3">Sort</span>
        <button
          onClick={() => setSortBy('overdue')}
          className={clsx(
            'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all',
            sortBy === 'overdue'
              ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
              : 'text-slate-600 hover:bg-slate-50'
          )}
        >
          Most overdue
        </button>
        <button
          onClick={() => setSortBy('total')}
          className={clsx(
            'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all',
            sortBy === 'total'
              ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
              : 'text-slate-600 hover:bg-slate-50'
          )}
        >
          Highest total
        </button>
      </div>

      <div className="card overflow-hidden border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Supplier</th>
                <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Current</th>
                <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-amber-700">1-30</th>
                <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-amber-700">31-60</th>
                <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-orange-700">61-90</th>
                <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-danger">90+</th>
                <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-700">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sortedSuppliers.map((s: any, i: number) => (
                <tr key={s.supplierId} className={i % 2 ? 'bg-slate-50/40' : 'bg-white'}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-900">{s.supplierName}</div>
                    <div className="text-2xs text-slate-500 mt-0.5 font-mono tabular-nums">
                      {s.invoices.length} invoice{s.invoices.length === 1 ? '' : 's'}
                    </div>
                  </td>
                  <BucketCell value={s.current} />
                  <BucketCell value={s.b1_30} color="text-amber-700" />
                  <BucketCell value={s.b31_60} color="text-amber-800" />
                  <BucketCell value={s.b61_90} color="text-orange-800" />
                  <BucketCell value={s.b90_plus} color="text-danger" bold />
                  <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-900 tabular-nums">
                    ${s.total.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 border-t-2 border-slate-300">
                <td className="px-4 py-3 eyebrow text-slate-700">Grand total</td>
                <BucketCell value={totals.current} foot />
                <BucketCell value={totals.b1_30} color="text-amber-800" foot />
                <BucketCell value={totals.b31_60} color="text-amber-900" foot />
                <BucketCell value={totals.b61_90} color="text-orange-900" foot />
                <BucketCell value={totals.b90_plus} color="text-danger" bold foot />
                <td className="px-4 py-3 text-right font-mono font-display font-bold text-lg text-slate-900 tabular-nums">
                  ${totals.total.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );
}

function StackedBucketBar({ totals }: { totals: any }) {
  const total = totals.total || 1;
  const segments = [
    { label: 'Current',    value: totals.current,  color: 'bg-slate-400' },
    { label: '1-30 days',  value: totals.b1_30,    color: 'bg-amber-400' },
    { label: '31-60 days', value: totals.b31_60,   color: 'bg-amber-600' },
    { label: '61-90 days', value: totals.b61_90,   color: 'bg-orange-600' },
    { label: '90+ days',   value: totals.b90_plus, color: 'bg-danger' },
  ];
  return (
    <div className="flex h-8 rounded-md overflow-hidden bg-slate-100">
      {segments.map((seg) =>
        seg.value > 0 ? (
          <div
            key={seg.label}
            className={clsx('h-full', seg.color)}
            style={{ width: `${(seg.value / total) * 100}%` }}
            title={`${seg.label}: ${fmtCurrency(seg.value)} (${((seg.value / total) * 100).toFixed(1)}%)`}
          />
        ) : null
      )}
    </div>
  );
}

function BucketLegend({
  label, value, color,
}: { label: string; value: number; color: string }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-2xs text-slate-600">
        <span className={clsx('w-2 h-2 rounded-sm', color)} />
        <span className="uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <div className="mt-1 font-mono font-bold text-slate-900 tabular-nums">
        ${value.toFixed(2)}
      </div>
    </div>
  );
}

function BucketCell({
  value, color = 'text-slate-700', bold, foot,
}: { value: number; color?: string; bold?: boolean; foot?: boolean }) {
  const classes = clsx(
    foot ? 'px-4 py-3' : 'px-4 py-2.5',
    'text-right font-mono tabular-nums',
    value > 0 ? color : 'text-slate-300',
    (bold || foot) && value > 0 ? 'font-bold' : value > 0 ? 'font-medium' : ''
  );
  return (
    <td className={classes}>
      {value > 0 ? `${fmtCurrency(value)}` : '—'}
    </td>
  );
}

function KpiTile({
  label, value, accent = 'default', sublabel,
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'finance' | 'sage' | 'amber' | 'danger';
  sublabel?: string;
}) {
  const bar = {
    default: 'bg-slate-200',
    finance: 'bg-finance-500',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden border-slate-200">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-slate-500">{label}</div>
      <div className="mt-3 text-2xl font-bold font-display text-slate-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {sublabel && (
        <div className="text-2xs text-slate-500 mt-0.5">{sublabel}</div>
      )}
    </div>
  );
}
